import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Tabs, Tab, Typography } from "@mui/material";

import MunicipalitiesView from "./MunicipalitiesView";
import MunicipalityPropertiesView from "./MunicipalityPropertiesView";
import AllPropertiesView from "./AllPropertiesView";
import SidebarResizeHandle from "../../shared/SidebarResizeHandle";
import styles from "./sideMenu.module.css";

function SideMenu({
  open,
  handleToggle,
  mapRef,
  setMarkerInfo,
  selectedMunicipality,
  setSelectedMunicipality,
  activeTab,
  setActiveTab,
  setCurrentProperties,
  selectedPortfolioId,
  width,
  onResize,
}) {
  const { t } = useTranslation();
  const handleTabChange = useCallback(
    (_, newValue) => {
      setActiveTab(newValue);
      setMarkerInfo?.(null); // Clear property marker when switching tabs
      setCurrentProperties?.([]); // Clear properties when switching tabs
      if (newValue !== 0) {
        setSelectedMunicipality?.(null);
      }
    },
    [
      setMarkerInfo,
      setSelectedMunicipality,
      setCurrentProperties,
      setActiveTab,
    ],
  );

  const handleMunicipalitySelect = useCallback(
    (municipality) => {
      setSelectedMunicipality?.(municipality);
    },
    [setSelectedMunicipality],
  );

  const handleBackToMunicipalities = useCallback(() => {
    setSelectedMunicipality?.(null);
    setMarkerInfo?.(null); // Clear property marker when going back
    setCurrentProperties?.([]); // Clear properties when going back
  }, [setSelectedMunicipality, setMarkerInfo, setCurrentProperties]);

  const hasActivePortfolio = Boolean(selectedPortfolioId);

  const renderContent = () => {
    if (!hasActivePortfolio) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t("portfolioMap.noPortfolioFound", {
            defaultValue: "No portfolio found for this client yet.",
          })}
        </Typography>
      );
    }

    if (activeTab === 0 && selectedMunicipality) {
      return (
        <MunicipalityPropertiesView
          municipality={selectedMunicipality}
          onBack={handleBackToMunicipalities}
          mapRef={mapRef}
          setMarkerInfo={setMarkerInfo}
          setCurrentProperties={setCurrentProperties}
          selectedPortfolioId={selectedPortfolioId}
        />
      );
    }

    if (activeTab === 0) {
      return (
        <MunicipalitiesView
          onMunicipalitySelect={handleMunicipalitySelect}
          mapRef={mapRef}
          selectedPortfolioId={selectedPortfolioId}
        />
      );
    }

    return (
      <AllPropertiesView
        mapRef={mapRef}
        setMarkerInfo={setMarkerInfo}
        setCurrentProperties={setCurrentProperties}
        selectedPortfolioId={selectedPortfolioId}
      />
    );
  };

  return (
    <div
      className={`${styles.container} ${open ? "" : styles.hidden}`}
      style={{ width }}
    >
      <SidebarResizeHandle onResize={onResize} />

      <IconButton onClick={handleToggle} className={styles.toggleButton}>
        {open ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>

      <div className={styles.content}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab
              label={t("portfolioMap.tabs.municipalities")}
              disabled={!hasActivePortfolio}
            />
            <Tab
              label={t("portfolioMap.tabs.properties")}
              disabled={!hasActivePortfolio}
            />
          </Tabs>
        </Box>

        {renderContent()}
      </div>
    </div>
  );
}

export default SideMenu;
